import React from "react";
import moment from "moment";
import { alpha, makeStyles } from "@material-ui/core/styles";
import { Button, Typography } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";

import { scheduleState } from "../../helpers/scheduleStatus";

const STATE_LABEL = { pending: "Pendente", sent: "Enviada", attention: "Precisa de atenção" };
// cores de texto com contraste suficiente sobre o fundo claro do chip
const STATE_COLOR = { pending: "#9A4A0B", sent: "#1E6B33", attention: "#B3261E" };

const WEEKDAYS = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];
const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

const useStyles = makeStyles((theme) => ({
  panel: {
    width: 330,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
    [theme.breakpoints.down("md")]: { width: "100%", maxHeight: 420 },
  },
  header: { padding: theme.spacing(2, 2, 1.5), borderBottom: `1px solid ${theme.palette.divider}` },
  weekday: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  dayRow: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 },
  day: { fontSize: 20, fontWeight: 700 },
  count: { fontSize: 13, color: theme.palette.text.secondary },
  list: { flex: 1, overflowY: "auto", padding: theme.spacing(1.5), display: "flex", flexDirection: "column", gap: theme.spacing(1) },
  item: {
    display: "flex",
    gap: theme.spacing(1.25),
    textAlign: "left",
    width: "100%",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: "transparent",
    color: theme.palette.text.primary,
    cursor: "pointer",
    font: "inherit",
    "&:hover": { borderColor: theme.palette.text.disabled },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
  },
  time: { width: 44, flexShrink: 0, fontSize: 14, fontWeight: 700 },
  body: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 },
  name: { fontSize: 14, fontWeight: 600 },
  text: { fontSize: 13, color: theme.palette.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  chip: { alignSelf: "flex-start", marginTop: 4, height: 20, padding: "0 8px", borderRadius: 10, fontSize: 11.5, fontWeight: 600, display: "inline-flex", alignItems: "center" },
  empty: { padding: theme.spacing(3, 1), textAlign: "center", color: theme.palette.text.secondary, fontSize: 14 },
  footer: { padding: theme.spacing(1.5), borderTop: `1px solid ${theme.palette.divider}` },
  add: { textTransform: "none", fontWeight: 600, borderRadius: 10, height: 40 },
}));

// Lista das mensagens do dia escolhido no calendário
const DayPanel = ({ day, schedules, onOpen, onCreate }) => {
  const classes = useStyles();
  const date = moment(day);
  const items = [...schedules].sort((a, b) => new Date(a.sendAt) - new Date(b.sendAt));
  const isPast = date.isBefore(moment(), "day");

  return (
    <aside className={classes.panel} aria-label="Mensagens do dia">
      <div className={classes.header}>
        <div className={classes.weekday}>{WEEKDAYS[date.day()]}</div>
        <div className={classes.dayRow}>
          <h2 className={classes.day} style={{ margin: 0 }}>
            {date.date()} de {MONTHS[date.month()]}
          </h2>
          <span className={classes.count}>
            {items.length} mensagem{items.length !== 1 ? "ns" : ""}
          </span>
        </div>
      </div>
      <div className={classes.list}>
        {items.length === 0 && <div className={classes.empty}>Nenhuma mensagem neste dia.</div>}
        {items.map((s) => {
          const state = scheduleState(s);
          const color = STATE_COLOR[state];
          return (
            <button key={s.id} type="button" className={classes.item} onClick={() => onOpen(s)}>
              <span className={classes.time} style={{ color: state === "attention" ? color : undefined }}>
                {moment(s.sendAt).format("HH:mm")}
              </span>
              <span className={classes.body}>
                <span className={classes.name}>{s.contact?.name || "Sem contato"}</span>
                <span className={classes.text}>{s.body}</span>
                <span className={classes.chip} style={{ backgroundColor: alpha(color, 0.14), color }}>
                  {STATE_LABEL[state]}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <div className={classes.footer}>
        <Button
          fullWidth
          color="primary"
          variant="outlined"
          className={classes.add}
          startIcon={<AddIcon />}
          disabled={isPast}
          onClick={onCreate}
        >
          {isPast ? "Dia que já passou" : "Agendar neste dia"}
        </Button>
      </div>
    </aside>
  );
};

export default DayPanel;
